/* © NXAPP Limited */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { describeError } from '@/lib/describeError'
import { useAuthContext } from '@/context/AuthContext'
import { Plus, Trash2, Star, ShieldCheck, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Tag, Pencil, Check as CheckIcon, X } from 'lucide-react'
import Spinner from '@/components/ui/Spinner'
import EmptyState from '@/components/ui/EmptyState'
import { Settings } from 'lucide-react'

// ── DNS deliverability check (client-side via Cloudflare DNS-over-HTTPS) ──────

interface DnsResult { spf: boolean; dkim: string | null; dmarc: boolean; mx: boolean }

async function dohQuery(name: string, type: string): Promise<{ type: number; data: string }[]> {
  try {
    const res = await fetch(
      `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(name)}&type=${type}`,
      { headers: { Accept: 'application/dns-json' } },
    )
    if (!res.ok) return []
    const json = await res.json() as { Status: number; Answer?: { type: number; data: string }[] }
    return json.Status === 0 ? (json.Answer ?? []) : []
  } catch { return [] }
}

// Common DKIM selectors across providers (Resend, SendGrid, Mailgun, Google, M365…)
const DKIM_SELECTORS = ['resend', 'default', 's1', 's2', 'k1', 'k2', 'selector1', 'selector2', 'google', 'mail', 'em', 'smtp', 'dkim', 'mx', 'protonmail']

async function checkDomainDns(domain: string): Promise<DnsResult> {
  const [spfAns, dmarcAns, mxAns, ...dkimAns] = await Promise.all([
    dohQuery(domain, 'TXT'),
    dohQuery(`_dmarc.${domain}`, 'TXT'),
    dohQuery(domain, 'MX'),
    ...DKIM_SELECTORS.map(sel => dohQuery(`${sel}._domainkey.${domain}`, 'TXT')),
  ])
  const spf   = spfAns.some(a => a.data.toLowerCase().includes('v=spf1'))
  const dmarc = dmarcAns.some(a => a.data.toLowerCase().includes('v=dmarc1'))
  const mx    = mxAns.some(a => a.type === 15)
  let dkim: string | null = null
  dkimAns.forEach((ans, i) => { if (!dkim && ans.length) dkim = DKIM_SELECTORS[i] })
  return { spf, dkim, dmarc, mx }
}

function DnsTick({ ok, label, detail, warnOnly }: { ok: boolean; label: string; detail?: string; warnOnly?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border font-medium ${
      ok ? 'bg-green-50 text-green-700 border-green-200'
        : warnOnly ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-red-50 text-red-600 border-red-200'}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : warnOnly ? <AlertTriangle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label}{detail ? ` (${detail})` : ''}
    </span>
  )
}

const schema = z.object({
  name: z.string().min(1),
  provider: z.enum(['resend', 'smtp']),
  from_email: z.string().email(),
  from_name: z.string().min(1),
  reply_to: z.string().email().optional().or(z.literal('')),
  company_name: z.string().optional(),
  company_address: z.string().optional(),
  company_website: z.string().optional(),
  brand_id: z.string().optional(),
  // 'edit' relaxes the credential requirement — blank means keep what's stored
  mode: z.enum(['create', 'edit']).default('create'),
  batch_size: z.string().optional(),
  daily_cap: z.string().optional(),
  throttle_ms: z.string().optional(),
  // Resend
  api_key: z.string().optional(),
  // SMTP
  smtp_host: z.string().optional(),
  smtp_port: z.string().optional(),
  smtp_username: z.string().optional(),
  smtp_password: z.string().optional(),
}).superRefine((v, ctx) => {
  const creating = v.mode !== 'edit'
  if (creating && v.provider === 'resend' && !v.api_key?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['api_key'], message: 'Resend API key required' })
  }
  if (v.provider === 'smtp') {
    if (!v.smtp_host?.trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['smtp_host'], message: 'SMTP host required' })
    if (!v.smtp_username?.trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['smtp_username'], message: 'Username required' })
    if (creating && !v.smtp_password?.trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['smtp_password'], message: 'Password required' })
  }
})
type ProfileForm = z.infer<typeof schema>


export default function SettingsPage() {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [dns, setDns] = useState<Record<string, DnsResult | 'loading'>>({})

  const runDnsCheck = async (profileId: string, fromEmail: string) => {
    const domain = fromEmail.split('@')[1]?.toLowerCase()
    if (!domain) return
    setDns(prev => ({ ...prev, [profileId]: 'loading' }))
    const result = await checkDomainDns(domain)
    setDns(prev => ({ ...prev, [profileId]: result }))
  }

  const { data: profiles, isLoading } = useQuery({
    queryKey: ['sending-profiles', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('sending_profiles').select('*').eq('user_id', user!.id)
      return data ?? []
    },
    enabled: !!user,
  })

  const [newBrand, setNewBrand] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBrand, setEditBrand] = useState<{ id: string; name: string } | null>(null)

  const { data: brands } = useQuery({
    queryKey: ['brands', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('brands').select('id, name').eq('user_id', user!.id).order('name')
      return (data ?? []) as { id: string; name: string }[]
    },
  })

  const addBrand = useMutation({
    mutationFn: async (name: string) => {
      const { error } = await supabase.from('brands').insert({ user_id: user!.id, name })
      if (error) throw error
    },
    onSuccess: () => { setNewBrand(''); qc.invalidateQueries({ queryKey: ['brands'] }) },
  })

  const renameBrand = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase.from('brands').update({ name }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { setEditBrand(null); qc.invalidateQueries({ queryKey: ['brands'] }) },
  })

  const delBrand = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('brands').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['brands'] }); qc.invalidateQueries({ queryKey: ['sending-profiles'] }) },
  })

  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<ProfileForm>({
    resolver: zodResolver(schema),
    defaultValues: { mode: 'create', provider: 'resend', smtp_port: '587', batch_size: '60', daily_cap: '', throttle_ms: '30' },
  })
  const provider = watch('provider')

  const saveProfile = useMutation({
    mutationFn: async (data: ProfileForm) => {
      // resend → raw API key · smtp → JSON {host, port, username, password}
      const hasNewCreds = data.provider === 'smtp'
        ? !!data.smtp_password?.trim()
        : !!data.api_key?.trim()
      const config = data.provider === 'smtp'
        ? JSON.stringify({
            host: data.smtp_host!.trim(),
            port: Number(data.smtp_port) || 587,
            username: data.smtp_username!.trim(),
            password: data.smtp_password!,
          })
        : data.api_key?.trim()

      const payload: Record<string, unknown> = {
        name: data.name,
        provider: data.provider,
        from_email: data.from_email,
        from_name: data.from_name,
        reply_to: data.reply_to?.trim() || null,
        company_name: data.company_name?.trim() || null,
        company_address: data.company_address?.trim() || null,
        company_website: data.company_website?.trim() || null,
        brand_id: data.brand_id || null,
        batch_size: Math.max(1, Number(data.batch_size) || 60),
        daily_cap: data.daily_cap?.trim() ? Math.max(1, Number(data.daily_cap)) : null,
        throttle_ms: Math.max(0, (Number(data.throttle_ms) || 0) * 1000),   // seconds → ms
      }

      if (editingId) {
        // Blank credentials on edit = keep the stored ones, so you can change a
        // From name without re-entering the SMTP password.
        if (hasNewCreds) payload.config_enc = config
        const { error } = await supabase.from('sending_profiles')
          .update(payload).eq('id', editingId)
        if (error) throw error
      } else {
        const { error } = await supabase.from('sending_profiles').insert({
          ...payload,
          user_id: user!.id,
          config_enc: config,   // stored as-is for now; encrypt in a Pages Function for prod
          is_default: !profiles?.length,
        })
        if (error) throw error
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sending-profiles'] })
      reset(); setShowAdd(false); setEditingId(null)
    },
  })

  const startEdit = (p: Record<string, unknown>) => {
    let host = '', port = '587', username = ''
    if (p.provider === 'smtp' && typeof p.config_enc === 'string') {
      try {
        const cfg = JSON.parse(p.config_enc)
        host = cfg.host ?? ''; port = String(cfg.port ?? 587); username = cfg.username ?? ''
      } catch { /* stored config unreadable — leave blank so it can be re-entered */ }
    }
    reset({
      mode: 'edit',
      name: (p.name as string) ?? '',
      provider: (p.provider as 'resend' | 'smtp') ?? 'resend',
      from_email: (p.from_email as string) ?? '',
      from_name: (p.from_name as string) ?? '',
      reply_to: (p.reply_to as string) ?? '',
      company_name: (p.company_name as string) ?? '',
      company_address: (p.company_address as string) ?? '',
      company_website: (p.company_website as string) ?? '',
      brand_id: (p.brand_id as string) ?? '',
      batch_size: String(p.batch_size ?? 60),
      daily_cap: p.daily_cap ? String(p.daily_cap) : '',
      throttle_ms: String(Math.round(Number(p.throttle_ms ?? 0) / 1000)),
      smtp_host: host, smtp_port: port, smtp_username: username, smtp_password: '',
      api_key: '',
    })
    setEditingId(p.id as string)
    setShowAdd(true)
  }

  const cancelForm = () => { reset({ mode: 'create', provider: 'resend', smtp_port: '587', batch_size: '60', daily_cap: '', throttle_ms: '30' }); setEditingId(null); setShowAdd(false) }

  const setDefault = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('sending_profiles').update({ is_default: false }).eq('user_id', user!.id)
      await supabase.from('sending_profiles').update({ is_default: true }).eq('id', id)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sending-profiles'] }),
  })

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('sending_profiles').delete().eq('id', id).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sending-profiles'] }),
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Configure your sending profiles and integrations</p>
      </div>

      {/* Brands */}
      <section className="space-y-3">
        <div>
          <h2 className="font-semibold text-gray-900">Brands</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            The identity a recipient consented to hear from. Sending profiles belong to a brand,
            and unsubscribes are scoped to it — so someone leaving one brand still receives the others.
          </p>
        </div>

        <div className="card p-4 space-y-3">
          {(brands ?? []).length > 0 && (
            <div className="space-y-1.5">
              {(brands ?? []).map(b => (
                <div key={b.id} className="flex items-center gap-2 text-sm">
                  <Tag className="w-3.5 h-3.5 text-gray-400" />
                  {editBrand?.id === b.id ? (
                    <>
                      <input
                        autoFocus
                        className="input flex-1 py-1 text-sm"
                        value={editBrand.name}
                        onChange={e => setEditBrand({ id: b.id, name: e.target.value })}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && editBrand.name.trim()) { e.preventDefault(); renameBrand.mutate({ id: b.id, name: editBrand.name.trim() }) }
                          if (e.key === 'Escape') setEditBrand(null)
                        }}
                      />
                      <button
                        onClick={() => editBrand.name.trim() && renameBrand.mutate({ id: b.id, name: editBrand.name.trim() })}
                        className="p-1 text-green-600 hover:bg-green-50 rounded" title="Save"
                      >
                        <CheckIcon className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditBrand(null)} className="p-1 text-gray-400 hover:bg-gray-50 rounded" title="Cancel">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-gray-800">{b.name}</span>
                      <span className="text-xs text-gray-400">
                        {(profiles ?? []).filter(p => p.brand_id === b.id).length} profile(s)
                      </span>
                      <button
                        onClick={() => setEditBrand({ id: b.id, name: b.name })}
                        className="p-1 text-gray-300 hover:text-brand-600 rounded"
                        title="Rename brand"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { if (confirm(`Delete brand "${b.name}"? Its profiles keep working but become unbranded.`)) delBrand.mutate(b.id) }}
                        className="p-1 text-gray-300 hover:text-red-500 rounded"
                        title="Delete brand"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="Brand name, e.g. NXAPP"
              value={newBrand}
              onChange={e => setNewBrand(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newBrand.trim()) { e.preventDefault(); addBrand.mutate(newBrand.trim()) } }}
            />
            <button
              onClick={() => newBrand.trim() && addBrand.mutate(newBrand.trim())}
              disabled={!newBrand.trim() || addBrand.isPending}
              className="btn-secondary text-sm"
            >
              <Plus className="w-4 h-4" /> Add brand
            </button>
          </div>
          {addBrand.isError && <p className="text-xs text-red-600 font-mono">{describeError(addBrand.error)}</p>}
        </div>
      </section>

      {/* Sending profiles */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Sending profiles</h2>
          <button onClick={() => (showAdd ? cancelForm() : setShowAdd(true))} className="btn-primary text-sm">
            <Plus className="w-4 h-4" /> Add profile
          </button>
        </div>

        {showAdd && (
          <div className="card p-5">
            <h3 className="font-semibold text-sm text-gray-900 mb-4">
              {editingId ? 'Edit sending profile' : 'New sending profile'}
            </h3>
            <form onSubmit={handleSubmit(d => saveProfile.mutate(d))} className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Profile name</label>
                <input className="input" placeholder="Main — Resend" {...register('name')} />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
              </div>
              <div>
                <label className="label">Provider</label>
                <select className="input" {...register('provider')}>
                  <option value="resend">Resend</option>
                  <option value="smtp">SMTP</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="label">Brand <span className="text-gray-400 font-normal">— who the recipient thinks they hear from</span></label>
                <select className="input" {...register('brand_id')}>
                  <option value="">— none (unsubscribes apply to everything) —</option>
                  {(brands ?? []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  Unsubscribes are recorded against the brand, so opting out of one brand leaves your
                  others untouched — and swapping provider never loses an opt-out.
                </p>
              </div>
              <div>
                <label className="label">From email</label>
                <input className="input" type="email" placeholder="newsletter@yourbrand.com" {...register('from_email')} />
                {errors.from_email && <p className="mt-1 text-xs text-red-600">{errors.from_email.message}</p>}
              </div>
              <div>
                <label className="label">From name</label>
                <input className="input" placeholder="NXAPP Weekly" {...register('from_name')} />
                {errors.from_name && <p className="mt-1 text-xs text-red-600">{errors.from_name.message}</p>}
              </div>
              <div className="col-span-2">
                <label className="label">Reply-to email <span className="text-gray-400 font-normal">(optional — where replies land)</span></label>
                <input className="input" type="email" placeholder="hello@yourbrand.com" {...register('reply_to')} />
                {errors.reply_to && <p className="mt-1 text-xs text-red-600">Enter a valid email or leave blank</p>}
              </div>

              {/* Footer identity — merged into every email's footer for this profile */}
              <div className="col-span-2 border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Footer identity <span className="normal-case font-normal text-gray-400">— shown in the email footer (legal requirement)</span></p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Company / brand name</label>
                    <input className="input" placeholder="NXAPP Limited" {...register('company_name')} />
                  </div>
                  <div>
                    <label className="label">Website</label>
                    <input className="input" placeholder="nxapp.co.uk" {...register('company_website')} />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Postal address</label>
                    <input className="input" placeholder="128 City Road, London, EC1V 2NX, UK" {...register('company_address')} />
                    <p className="mt-1 text-xs text-gray-400">A real postal address in the footer is legally required for marketing email. Templates merge these via {'{{companyName}}'} / {'{{companyAddress}}'}.</p>
                  </div>
                </div>
              </div>

              {provider === 'resend' ? (
                <div className="col-span-2">
                  <label className="label">Resend API key</label>
                  <input className="input font-mono" type="password" placeholder={editingId ? 'Leave blank to keep the saved key' : 're_…'} {...register('api_key')} />
                  {errors.api_key && <p className="mt-1 text-xs text-red-600">{errors.api_key.message}</p>}
                </div>
              ) : (
                <>
                  <div>
                    <label className="label">SMTP host</label>
                    <input className="input" placeholder="smtp.yourprovider.com" {...register('smtp_host')} />
                    {errors.smtp_host && <p className="mt-1 text-xs text-red-600">{errors.smtp_host.message}</p>}
                  </div>
                  <div>
                    <label className="label">Port</label>
                    <select className="input" {...register('smtp_port')}>
                      <option value="587">587 (STARTTLS — recommended)</option>
                      <option value="465">465 (TLS)</option>
                      <option value="2525">2525 (STARTTLS)</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-400">Port 25 is blocked on Cloudflare — use 587 or 465.</p>
                  </div>
                  <div>
                    <label className="label">SMTP username</label>
                    <input className="input" placeholder="usually the from email" {...register('smtp_username')} />
                    {errors.smtp_username && <p className="mt-1 text-xs text-red-600">{errors.smtp_username.message}</p>}
                  </div>
                  <div>
                    <label className="label">SMTP password</label>
                    <input className="input font-mono" type="password" placeholder={editingId ? 'Leave blank to keep the saved password' : ''} {...register('smtp_password')} />
                    {errors.smtp_password && <p className="mt-1 text-xs text-red-600">{errors.smtp_password.message}</p>}
                  </div>
                </>
              )}
              <p className="col-span-2 text-xs text-gray-400 -mt-2">Credentials are stored server-side and only used when sending.</p>

              {/* Send-rate controls */}
              <div className="col-span-2 border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Send rate (protects your domain reputation)</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">Per hour</label>
                    <input className="input" type="number" min={1} {...register('batch_size')} />
                    <p className="mt-1 text-[10px] text-gray-400">Max emails per rolling hour. Ceiling — actual pace also depends on the cron interval.</p>
                  </div>
                  <div>
                    <label className="label">Daily cap</label>
                    <input className="input" type="number" min={1} placeholder="unlimited" {...register('daily_cap')} />
                    <p className="mt-1 text-[10px] text-gray-400">Max/24h. Blank = unlimited. Use for warm-up.</p>
                  </div>
                  <div>
                    <label className="label">Throttle (sec)</label>
                    <input className="input" type="number" min={0} {...register('throttle_ms')} />
                    <p className="mt-1 text-[10px] text-gray-400">Pause between each email. 0 = none.</p>
                  </div>
                </div>
              </div>
              {saveProfile.isError && (() => {
                const text = describeError(saveProfile.error)
                return (
                  <div className="col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <p className="text-xs font-semibold text-red-800">Could not save profile</p>
                    <p className="mt-0.5 text-xs text-red-700 font-mono break-all whitespace-pre-wrap">{text}</p>
                    {/(company_name|company_address|company_website|batch_size|daily_cap|throttle_ms|schema cache|does not exist|PGRST204)/i.test(text) && (
                      <p className="mt-1.5 text-xs text-red-700">
                        Looks like a missing column. Run any unapplied files in <code>supabase/migrations/</code> in the Supabase SQL editor — they are all <code>if not exists</code>, so re-running old ones is safe — then <code>notify pgrst, 'reload schema';</code> and try again.
                      </p>
                    )}
                  </div>
                )
              })()}
              <div className="col-span-2 flex gap-2 justify-end">
                <button type="button" onClick={cancelForm} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  {editingId ? 'Save changes' : 'Save profile'}
                </button>
              </div>
            </form>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : !profiles?.length ? (
          <EmptyState icon={Settings} title="No sending profiles" description="Add a Resend API key to start sending." />
        ) : (
          <div className="space-y-2">
            {profiles.map(p => {
              const result = dns[p.id]
              return (
              <div key={p.id} className="card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-sm">{p.name}</p>
                      {p.is_default && <span className="badge bg-brand-100 text-brand-700 text-xs">Default</span>}
                      {p.brand_id
                        ? <span className="badge bg-indigo-50 text-indigo-700 text-xs">
                            {(brands ?? []).find(b => b.id === p.brand_id)?.name ?? 'Brand'}
                          </span>
                        : <span className="badge bg-amber-50 text-amber-700 text-xs" title="Unsubscribes from this profile will suppress the contact everywhere">
                            No brand
                          </span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {p.from_name} &lt;{p.from_email}&gt; · {p.provider === 'smtp' ? 'SMTP' : 'Resend'}
                      {p.reply_to && <span className="text-gray-400"> · replies → {p.reply_to}</span>}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {(p.batch_size ?? 60)}/hour · {p.daily_cap ? `${p.daily_cap}/day` : 'no daily cap'}{(p.throttle_ms ?? 0) > 0 ? ` · ${Math.round((p.throttle_ms ?? 0) / 1000)}s throttle` : ''}
                    </p>
                    {p.company_name && (
                      <p className="text-[10px] text-gray-400 mt-0.5">Footer: {p.company_name}{p.company_address ? ` · ${p.company_address}` : ''}{!p.company_address ? ' · ⚠ add a postal address' : ''}</p>
                    )}
                    {!p.company_name && <p className="text-[10px] text-amber-500 mt-0.5">⚠ No footer identity set — add company name + address</p>}
                  </div>
                  <div className="flex gap-1 items-center">
                    <button onClick={() => runDnsCheck(p.id, p.from_email)} disabled={result === 'loading'}
                      className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-900 px-2.5 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                      title="Check SPF, DKIM, DMARC and MX records on the sending domain">
                      {result === 'loading' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      Check DNS
                    </button>
                    <button onClick={() => startEdit(p)} className="p-1.5 text-gray-400 hover:text-brand-600 rounded" title="Edit profile">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {!p.is_default && (
                      <button onClick={() => setDefault.mutate(p.id)} className="p-1.5 text-gray-400 hover:text-brand-600 rounded" title="Set as default">
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => confirm('Delete profile?') && del.mutate(p.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {result && result !== 'loading' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-1.5">
                    <DnsTick ok={result.spf} label="SPF" />
                    <DnsTick ok={!!result.dkim} label="DKIM" detail={result.dkim ?? undefined} warnOnly />
                    <DnsTick ok={result.dmarc} label="DMARC" />
                    <DnsTick ok={result.mx} label="MX" />
                    <span className="text-[10px] text-gray-400 ml-1">
                      {result.spf && result.dkim && result.dmarc
                        ? 'Deliverability records look good ✓'
                        : !result.dkim
                          ? 'DKIM not found via common selectors — it may still be set with a custom selector'
                          : 'Add the missing records at your DNS provider, then re-check'}
                    </span>
                  </div>
                )}
              </div>
            )})}
          </div>
        )}
      </section>

      {/* Deliverability checklist */}
      <section className="card p-5 space-y-3">
        <h2 className="font-semibold text-gray-900">Deliverability checklist</h2>
        {[
          'SPF record on your sending domain',
          'DKIM keys added (Resend generates these for verified domains)',
          'DMARC policy set (p=none to start)',
          'Sending from a subdomain (e.g. news.yourbrand.com), not main domain',
          'Resend domain verified',
          'Unsubscribe links in every email',
        ].map(item => (
          <label key={item} className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
            {item}
          </label>
        ))}
      </section>
    </div>
  )
}
